import { useState, useEffect, useRef } from "react";
import { apiService } from "../api/apiService";
import { hasLocalIcon } from "../utils/coinIcons";
import { useHyperliquidWebSocket } from "./useHyperliquidWebSocket";

export const useTradingTickers = (
  refreshInterval = 10000,
  useWebSocket = true,
) => {
  const [tickers, setTickers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const assetCtxsRef = useRef([]);
  const symbolToIndexMapRef = useRef({});
  const lastUpdateRef = useRef(0);

  const ws = useHyperliquidWebSocket({
    autoConnect: useWebSocket,
    log: false,
  });

  const fetchAllTickers = async () => {
    try {
      setError(null);

      // Obtener precios actuales y contexto de meta
      const [allMidsResponse, metaResponse] = await Promise.all([
        apiService.getAllMids(),
        apiService.getMetaAndAssetCtxs(),
      ]);

      const allPrices = allMidsResponse.data || allMidsResponse;
      const metaData = metaResponse.data || metaResponse;

      let universe = [];
      let assetCtxs = [];

      if (Array.isArray(metaData)) {
        universe = Array.isArray(metaData[0]) ? metaData[0] : [];
        assetCtxs = Array.isArray(metaData[1])
          ? metaData[1]
          : typeof metaData[1] === "object"
            ? Object.values(metaData[1])
            : [];
      } else if (metaData && typeof metaData === "object") {
        universe = Array.isArray(metaData.universe) ? metaData.universe : [];
        assetCtxs = Array.isArray(metaData.assetCtxs)
          ? metaData.assetCtxs
          : typeof metaData.assetCtxs === "object"
            ? Object.values(metaData.assetCtxs)
            : [];
      }

      const symbolToIndexMap = {};

      if (Array.isArray(universe) && universe.length > 0) {
        universe.forEach((coin, index) => {
          const coinName = coin?.name || coin;
          if (coinName) {
            symbolToIndexMap[coinName] = index;
            symbolToIndexMap[coinName.toUpperCase()] = index;
          }
        });
      } else {
        const allPricesKeys = Object.keys(allPrices || {}).filter(
          (key) => !key.startsWith("@") && !/^\d+$/.test(key),
        );

        allPricesKeys.forEach((key, idx) => {
          if (idx < assetCtxs.length) {
            symbolToIndexMap[key] = idx;
            symbolToIndexMap[key.toUpperCase()] = idx;
          }
        });
      }

      assetCtxsRef.current = assetCtxs;
      symbolToIndexMapRef.current = symbolToIndexMap;

      // Obtener todos los símbolos disponibles dinámicamente
      let availableSymbols = [];

      if (Array.isArray(universe) && universe.length > 0) {
        availableSymbols = universe
          .map((coin) => {
            return (
              coin?.name ||
              coin?.symbol ||
              (typeof coin === "string" ? coin : null)
            );
          })
          .filter(Boolean)
          .filter((name) => {
            const nameStr = String(name).toUpperCase();
            return (
              !nameStr.startsWith("@") &&
              !/^\d+$/.test(nameStr) &&
              nameStr.length >= 2 &&
              nameStr.length <= 10
            );
          })
          .map((s) => s.toUpperCase());
      } else {
        availableSymbols = Object.keys(allPrices || {})
          .filter((key) => {
            const upperKey = key.toUpperCase();
            return (
              !upperKey.startsWith("@") &&
              !/^\d+$/.test(upperKey) &&
              upperKey.length >= 2 &&
              upperKey.length <= 10 &&
              /^[A-Z0-9]+$/.test(upperKey)
            );
          })
          .map((key) => key.toUpperCase());
      }

      const HYPERLIQUID_SYMBOLS = [
        "BTC",
        "ETH",
        "SOL",
        "AVAX",
        "MATIC",
        "ATOM",
        "LINK",
        "UNI",
        "AAVE",
        "HL",
        "HYPE",
        "ASTER",
        "PEPE",
        "FLOKI",
        "ZEC",
        "BONK",
      ];
      if (availableSymbols.length === 0) {
        console.warn(
          "[useTradingTickers] No symbols found from API, using hardcoded list",
        );
        availableSymbols = HYPERLIQUID_SYMBOLS;
      }

      // Lista de símbolos permitidos incluso sin icono local
      // Incluye versiones con prefijo "K" que usa Hyperliquid para algunos tokens
      const allowedSymbolsWithoutIcon = [
        "HL",
        "HYPE",
        "ASTER",
        "PEPE",
        "FLOKI",
        "ZEC",
        "BONK",
        "KBONK",
        "KPEPE",
        "KFLOKI",
        "KDOGS",
        "KLUNC",
        "KNEIRO",
        "KSHIB", // Versiones con prefijo K de Hyperliquid
      ];

      const basicTickers = availableSymbols
        .filter((symbol) => {
          const upperSymbol = String(symbol).toUpperCase().trim();
          const hasIcon = hasLocalIcon(upperSymbol);
          const isAllowedWithoutIcon =
            allowedSymbolsWithoutIcon.includes(upperSymbol);
          const isValid =
            !upperSymbol.startsWith("@") &&
            !/^\d+$/.test(upperSymbol) &&
            upperSymbol.length >= 2 &&
            upperSymbol.length <= 10 &&
            /^[A-Z0-9]+$/.test(upperSymbol) &&
            (hasIcon || isAllowedWithoutIcon);

          // Token filtrado si no tiene icono y no está en la lista permitida
          return isValid;
        })
        .map((symbol) => {
          const coinId = symbol.toLowerCase();
          const pair = `${symbol}/USDC`;
          const upperSymbol = symbol.toUpperCase();

          // Buscar precio en diferentes formatos
          // Para tokens "K", también buscar con primera letra minúscula (ej: kBONK)
          const kTokenFormat =
            upperSymbol.startsWith("K") && upperSymbol.length > 1
              ? "k" + upperSymbol.substring(1)
              : null;

          const currentPrice = parseFloat(
            allPrices[symbol] ||
              allPrices[upperSymbol] ||
              allPrices[coinId] ||
              allPrices[symbol.toLowerCase()] ||
              (kTokenFormat ? allPrices[kTokenFormat] : null) ||
              0,
          );

          let ctx = {};
          let ctxIndex =
            symbolToIndexMap[symbol] ?? symbolToIndexMap[coinId] ?? -1;

          if (
            ctxIndex >= 0 &&
            Array.isArray(assetCtxs) &&
            assetCtxs[ctxIndex]
          ) {
            const candidateCtx = assetCtxs[ctxIndex];
            const ctxMarkPx = parseFloat(candidateCtx.markPx || 0);
            const priceDiff =
              ctxMarkPx > 0
                ? Math.abs(ctxMarkPx - currentPrice) /
                  Math.max(ctxMarkPx, currentPrice)
                : 1;

            if (priceDiff < 0.1) {
              ctx = candidateCtx;
            } else {
              ctxIndex = -1;
            }
          }

          if (!ctx || Object.keys(ctx).length === 0) {
            let closestIndex = -1;
            let closestDiff = Infinity;

            assetCtxs.forEach((c, idx) => {
              const markPx = parseFloat(c.markPx || 0);
              if (markPx > 0 && currentPrice > 0) {
                const diff =
                  Math.abs(markPx - currentPrice) /
                  Math.max(markPx, currentPrice);
                if (diff < 0.05 && diff < closestDiff) {
                  closestDiff = diff;
                  closestIndex = idx;
                }
              }
            });

            if (closestIndex >= 0) {
              ctxIndex = closestIndex;
              ctx = assetCtxs[closestIndex];
            }
          }

          const prevDayPx = parseFloat(ctx.prevDayPx || 0);

          let change24h = 0;
          let change24hPercent = 0;

          if (prevDayPx > 0 && currentPrice > 0) {
            const priceDiff =
              Math.abs(currentPrice - prevDayPx) /
              Math.max(currentPrice, prevDayPx);

            if (priceDiff > 0.5) {
              console.warn(
                `[useTradingTickers] ${symbol}: prevDayPx (${prevDayPx}) parece incorrecto comparado con currentPrice (${currentPrice}). Diferencia: ${(priceDiff * 100).toFixed(2)}%. Usando 0% como cambio.`,
              );
              change24h = 0;
              change24hPercent = 0;
            } else {
              change24h = currentPrice - prevDayPx;
              change24hPercent = (change24h / prevDayPx) * 100;

              if (Math.abs(change24hPercent) > 1000) {
                console.warn(
                  `[useTradingTickers] ${symbol}: Porcentaje extremo detectado (${change24hPercent.toFixed(2)}%). prevDayPx: ${prevDayPx}, currentPrice: ${currentPrice}. Usando 0% como cambio.`,
                );
                change24h = 0;
                change24hPercent = 0;
              }
            }
          }

          const volume24h = parseFloat(ctx.dayNtlVlm || 0);
          const fundingRate = parseFloat(ctx.funding || 0);

          console.log(`[useTradingTickers] ${symbol}:`, {
            currentPrice,
            prevDayPx,
            change24h: change24h.toFixed(2),
            change24hPercent: change24hPercent.toFixed(2) + "%",
            volume24h,
            fundingRate,
            ctxIndex,
            isValid:
              prevDayPx > 0 &&
              Math.abs(
                (currentPrice - prevDayPx) / Math.max(currentPrice, prevDayPx),
              ) <= 0.5,
            source: "allPrices from getAllMids()",
            timestamp: new Date().toISOString(),
            tickerLast: currentPrice,
          });

          return {
            symbol: pair,
            last: currentPrice,
            change24h: change24h,
            change24hPercent: change24hPercent,
            volume24h: volume24h,
            high24h: parseFloat(ctx.high24h || 0),
            low24h: parseFloat(ctx.low24h || 0),
            marketCap: 0,
            fundingRate: fundingRate,
          };
        })
        .filter((ticker) => {
          // Para tokens objetivo, permitir incluso si precio es 0 (pueden estar recién listados)
          const symbol = ticker.symbol.split("/")[0];
          const isTargetToken = [
            "HL",
            "HYPE",
            "ASTER",
            "PEPE",
            "FLOKI",
            "ZEC",
            "BONK",
            "KBONK",
            "KPEPE",
            "KFLOKI",
            "KDOGS",
            "KLUNC",
            "KNEIRO",
            "KSHIB",
          ].includes(symbol);
          if (isTargetToken && ticker.last === 0) {
            // Asignar un precio temporal muy pequeño para que pase el filtro
            ticker.last = 0.000001;
          }
          return ticker.last > 0;
        });

      // Ordenar por precio descendente por defecto
      basicTickers.sort((a, b) => (b.last || 0) - (a.last || 0));

      setTickers(basicTickers);
      setLoading(false);
    } catch (err) {
      console.error("[useTradingTickers] Error fetching tickers:", err);
      setError(err.message || "Error al obtener datos de trading");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllTickers();

    const interval = setInterval(() => {
      fetchAllTickers();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [refreshInterval]);

  useEffect(() => {
    if (!useWebSocket || !ws.isConnected || tickers.length === 0) {
      return;
    }

    const unsubscribe = ws.subscribeAllMids((allMids) => {
      if (!allMids || Object.keys(allMids).length === 0) return;

      const now = Date.now();
      if (now - lastUpdateRef.current < 100) return;
      lastUpdateRef.current = now;

      setTickers((prevTickers) => {
        return prevTickers.map((ticker) => {
          const symbol = ticker.symbol.split("/")[0];
          const upperSymbol = symbol.toUpperCase();

          const kTokenFormat =
            upperSymbol.startsWith("K") && upperSymbol.length > 1
              ? "k" + upperSymbol.substring(1)
              : null;

          const newPrice = parseFloat(
            allMids[symbol] ||
              allMids[upperSymbol] ||
              allMids[symbol.toLowerCase()] ||
              (kTokenFormat ? allMids[kTokenFormat] : null) ||
              ticker.last,
          );

          if (!newPrice || newPrice <= 0 || newPrice === ticker.last) {
            return ticker;
          }

          const assetCtxs = assetCtxsRef.current;
          const symbolToIndexMap = symbolToIndexMapRef.current;

          let ctx = {};
          let ctxIndex =
            symbolToIndexMap[symbol] ??
            symbolToIndexMap[symbol.toLowerCase()] ??
            -1;

          if (
            ctxIndex >= 0 &&
            Array.isArray(assetCtxs) &&
            assetCtxs[ctxIndex]
          ) {
            const candidateCtx = assetCtxs[ctxIndex];
            const ctxMarkPx = parseFloat(candidateCtx.markPx || 0);
            const priceDiff =
              ctxMarkPx > 0
                ? Math.abs(ctxMarkPx - newPrice) / Math.max(ctxMarkPx, newPrice)
                : 1;

            if (priceDiff < 0.1) {
              ctx = candidateCtx;
            } else {
              ctxIndex = -1;
            }
          }

          if (!ctx || Object.keys(ctx).length === 0) {
            let closestIndex = -1;
            let closestDiff = Infinity;

            assetCtxs.forEach((c, idx) => {
              const markPx = parseFloat(c.markPx || 0);
              if (markPx > 0 && newPrice > 0) {
                const diff =
                  Math.abs(markPx - newPrice) / Math.max(markPx, newPrice);
                if (diff < 0.05 && diff < closestDiff) {
                  closestDiff = diff;
                  closestIndex = idx;
                }
              }
            });

            if (closestIndex >= 0) {
              ctxIndex = closestIndex;
              ctx = assetCtxs[closestIndex];
            }
          }

          const prevDayPx = parseFloat(ctx.prevDayPx || 0);

          let change24h = ticker.change24h;
          let change24hPercent = ticker.change24hPercent;

          if (prevDayPx > 0 && newPrice > 0) {
            const priceDiff =
              Math.abs(newPrice - prevDayPx) / Math.max(newPrice, prevDayPx);

            if (priceDiff <= 0.5) {
              change24h = newPrice - prevDayPx;
              change24hPercent = (change24h / prevDayPx) * 100;

              if (Math.abs(change24hPercent) > 1000) {
                change24h = 0;
                change24hPercent = 0;
              }
            }
          }

          return {
            ...ticker,
            last: newPrice,
            change24h: change24h,
            change24hPercent: change24hPercent,
          };
        });
      });
    });

    return unsubscribe;
  }, [useWebSocket, ws.isConnected, ws, tickers.length]);

  return {
    tickers,
    loading,
    error,
    refetch: fetchAllTickers,
  };
};
